/**
 * Number formatting for chart labels.
 *
 * The kit never formats a number itself: every label goes through the widget's
 * `useToolNumber()` formatter, passed in as `fmt`, so a German page reads 1.234,5 and a
 * French one 1 234,5 without the kit knowing about locales. These helpers only decide
 * how many decimals a label shows.
 */

/** The shape of `useToolNumber()`. */
export type NumberFormat = (
  value: number,
  options?: Intl.NumberFormatOptions
) => string;

/** The typographic minus the charts print in notation (equations, stems). */
export const MINUS = "−";

/** A tick formatter that gives every label on an axis the same decimals. */
export function tickFormat(
  fmt: NumberFormat,
  decimals: number
): (value: number) => string {
  return (value) =>
    fmt(value, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
}

/** A data value as a label: up to `maxDecimals` decimals, trailing zeros dropped. */
export function valueText(
  fmt: NumberFormat,
  value: number,
  maxDecimals = 2
): string {
  return fmt(value, { maximumFractionDigits: maxDecimals });
}

/** A share (0–1) as a percentage with at most one decimal: 0.315 → 31.5%. */
export function percentText(fmt: NumberFormat, share: number): string {
  return fmt(share, { style: "percent", maximumFractionDigits: 1 });
}

/** True where the locale writes a decimal comma, so a list needs another separator. */
export function usesDecimalComma(fmt: NumberFormat): boolean {
  return fmt(1.5).includes(",");
}
